/**
 * ScenarioPlanner
 *
 * An interactive component for comparing different strategic scenarios
 * and visualizing potential outcomes based on different factors.
 *
 * Primary responsibilities:
 * - Display multiple potential business scenarios
 * - Compare outcome probabilities across scenarios
 * - Visualize impact of different factors on scenarios
 * - Support decision-making through scenario analysis
 */

import React, { useState, useMemo } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Label } from '@/components/ui/label'

// Types
export interface Factor {
  id: string
  name: string
  description: string
  impact: 'high' | 'medium' | 'low'
  uncertainty: 'high' | 'medium' | 'low'
  weight: number
}

export interface ScenarioOutcome {
  id: string
  name: string
  description: string
  probability: number
  impact: 'positive' | 'negative' | 'neutral'
  magnitude: number
}

export interface Scenario {
  id: string
  name: string
  description: string
  factors: Factor[]
  outcomes: ScenarioOutcome[]
  probability: number
}

export interface ScenarioPlannerData {
  title: string
  description: string
  timeframe: string
  scenarios: Scenario[]
  activeFactors: string[] // IDs of factors to display/compare across scenarios
  compareDimension: 'probability' | 'impact' | 'factors' | 'outcomes' // Current comparison view
}

// Utility Functions
const getImpactColor = (impact: string): string => {
  switch (impact) {
    case 'high':
    case 'positive':
      return 'bg-green-100 text-green-800'
    case 'medium':
    case 'neutral':
      return 'bg-yellow-100 text-yellow-800'
    case 'low':
    case 'negative':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatProbability = (probability: number): string => {
  return `${Math.round(probability * 100)}%`
}

export const ScenarioPlanner: React.FC<{ data: ScenarioPlannerData }> = ({ data }) => {
  const [activeScenarioId, setActiveScenarioId] = useState<string>(
    data.scenarios.length > 0 ? data.scenarios[0].id : ''
  )
  const [compareDimension, setCompareDimension] = useState<
    'probability' | 'impact' | 'factors' | 'outcomes'
  >(data.compareDimension || 'probability')
  const [activeFactors, setActiveFactors] = useState<string[]>(data.activeFactors || [])
  const [adjustedFactorWeights, setAdjustedFactorWeights] = useState<Record<string, number>>({})

  // Get active scenario
  const activeScenario = useMemo(() => {
    return data.scenarios.find((s) => s.id === activeScenarioId) || data.scenarios[0]
  }, [activeScenarioId, data.scenarios])

  // Get all unique factors across all scenarios
  const allFactors = useMemo(() => {
    const factors = new Map<string, Factor>()

    data.scenarios.forEach((scenario) => {
      scenario.factors.forEach((factor) => {
        if (!factors.has(factor.id)) {
          factors.set(factor.id, factor)
        }
      })
    })

    return Array.from(factors.values())
  }, [data.scenarios])

  // Toggle active factor
  const toggleFactor = (factorId: string) => {
    setActiveFactors((prev) =>
      prev.includes(factorId) ? prev.filter((id) => id !== factorId) : [...prev, factorId]
    )
  }

  // Adjust factor weight
  const adjustFactorWeight = (factorId: string, weight: number) => {
    setAdjustedFactorWeights((prev) => ({
      ...prev,
      [factorId]: weight
    }))
  }

  // Get factor weight (adjusted or original)
  const getFactorWeight = (factor: Factor): number => {
    return adjustedFactorWeights[factor.id] !== undefined
      ? adjustedFactorWeights[factor.id]
      : factor.weight
  }

  // Render scenario list
  const renderScenarioList = () => (
    <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
      {data.scenarios.map((scenario) => (
        <Card
          key={scenario.id}
          className={`cursor-pointer transition-colors hover:bg-slate-50 ${
            scenario.id === activeScenarioId ? 'border-2 border-blue-500' : ''
          }`}
          onClick={() => setActiveScenarioId(scenario.id)}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{scenario.name}</CardTitle>
            <CardDescription>
              Probability: {formatProbability(scenario.probability)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="line-clamp-3 text-sm text-gray-600">{scenario.description}</p>
          </CardContent>
          <CardFooter className="pt-0">
            <div className="flex flex-wrap gap-2">
              {scenario.factors.slice(0, 3).map((factor) => (
                <Badge key={factor.id} variant="outline" className={getImpactColor(factor.impact)}>
                  {factor.name}
                </Badge>
              ))}
              {scenario.factors.length > 3 && (
                <Badge variant="outline">+{scenario.factors.length - 3}</Badge>
              )}
            </div>
          </CardFooter>
        </Card>
      ))}
    </div>
  )

  // Render factors list
  const renderFactorsList = () => (
    <div className="mb-6">
      <h3 className="mb-2 text-lg font-medium">Key Factors</h3>
      <div className="flex flex-wrap gap-2">
        {allFactors.map((factor) => (
          <Badge
            key={factor.id}
            variant={activeFactors.includes(factor.id) ? 'default' : 'outline'}
            className="cursor-pointer transition-colors hover:bg-slate-200"
            onClick={() => toggleFactor(factor.id)}
          >
            {factor.name}
          </Badge>
        ))}
      </div>
    </div>
  )

  // Render scenario details
  const renderScenarioDetails = () => (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{activeScenario.name}</CardTitle>
        <CardDescription>
          {data.timeframe} • Probability: {formatProbability(activeScenario.probability)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="mb-4">{activeScenario.description}</p>

        <Tabs defaultValue="factors" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="factors">Key Factors</TabsTrigger>
            <TabsTrigger value="outcomes">Potential Outcomes</TabsTrigger>
          </TabsList>

          <TabsContent value="factors">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factor</TableHead>
                  <TableHead>Impact</TableHead>
                  <TableHead>Uncertainty</TableHead>
                  <TableHead>Weight</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeScenario.factors.map((factor) => (
                  <TableRow key={factor.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{factor.name}</div>
                        <div className="text-xs text-gray-500">{factor.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getImpactColor(factor.impact)}>{factor.impact}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getImpactColor(factor.uncertainty)}>
                        {factor.uncertainty}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="w-24">
                        <Slider
                          defaultValue={[factor.weight]}
                          max={10}
                          step={1}
                          onValueChange={(value) => adjustFactorWeight(factor.id, value[0])}
                        />
                        <div className="mt-1 text-center text-xs">{getFactorWeight(factor)}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="outcomes">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Probability</TableHead>
                  <TableHead>Impact</TableHead>
                  <TableHead>Magnitude</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeScenario.outcomes.map((outcome) => (
                  <TableRow key={outcome.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{outcome.name}</div>
                        <div className="text-xs text-gray-500">{outcome.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>{formatProbability(outcome.probability)}</TableCell>
                    <TableCell>
                      <Badge className={getImpactColor(outcome.impact)}>{outcome.impact}</Badge>
                    </TableCell>
                    <TableCell>{outcome.magnitude}/10</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )

  // Render comparison view
  const renderComparison = () => {
    switch (compareDimension) {
      case 'probability':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Scenario Probability Comparison</CardTitle>
              <CardDescription>Relative likelihood of each scenario occurring</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.scenarios.map((scenario) => (
                  <div key={scenario.id}>
                    <div className="mb-1 flex justify-between">
                      <span className="text-sm font-medium">{scenario.name}</span>
                      <span className="text-sm text-gray-500">
                        {formatProbability(scenario.probability)}
                      </span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2.5 rounded-full bg-blue-600"
                        style={{ width: `${scenario.probability * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )

      case 'factors':
        // Only show selected factors
        const factors =
          activeFactors.length > 0 ? activeFactors : allFactors.slice(0, 5).map((f) => f.id)

        return (
          <Card>
            <CardHeader>
              <CardTitle>Factor Comparison Across Scenarios</CardTitle>
              <CardDescription>How key factors vary between scenarios</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Factor</TableHead>
                    {data.scenarios.map((scenario) => (
                      <TableHead key={scenario.id}>{scenario.name}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {factors.map((factorId) => {
                    const factorName = allFactors.find((f) => f.id === factorId)?.name || factorId

                    return (
                      <TableRow key={factorId}>
                        <TableCell>{factorName}</TableCell>
                        {data.scenarios.map((scenario) => {
                          const factor = scenario.factors.find((f) => f.id === factorId)

                          return (
                            <TableCell key={`${scenario.id}-${factorId}`}>
                              {factor ? (
                                <Badge className={getImpactColor(factor.impact)}>
                                  {factor.impact}
                                </Badge>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )

      case 'outcomes':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Outcome Comparison</CardTitle>
              <CardDescription>Comparing potential outcomes across scenarios</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scenario</TableHead>
                    <TableHead>Most Likely Outcome</TableHead>
                    <TableHead>Probability</TableHead>
                    <TableHead>Impact</TableHead>
                    <TableHead>Magnitude</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.scenarios.map((scenario) => {
                    // Find most likely outcome
                    const mostLikelyOutcome = [...scenario.outcomes].sort(
                      (a, b) => b.probability - a.probability
                    )[0]

                    return (
                      <TableRow key={scenario.id}>
                        <TableCell>{scenario.name}</TableCell>
                        <TableCell>
                          {mostLikelyOutcome ? mostLikelyOutcome.name : 'No outcomes defined'}
                        </TableCell>
                        <TableCell>
                          {mostLikelyOutcome
                            ? formatProbability(mostLikelyOutcome.probability)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {mostLikelyOutcome ? (
                            <Badge className={getImpactColor(mostLikelyOutcome.impact)}>
                              {mostLikelyOutcome.impact}
                            </Badge>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {mostLikelyOutcome ? `${mostLikelyOutcome.magnitude}/10` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )

      case 'impact':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Impact Analysis</CardTitle>
              <CardDescription>Evaluating the potential impact of each scenario</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scenario</TableHead>
                    <TableHead>Positive Outcomes</TableHead>
                    <TableHead>Neutral Outcomes</TableHead>
                    <TableHead>Negative Outcomes</TableHead>
                    <TableHead>Net Impact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.scenarios.map((scenario) => {
                    const positiveOutcomes = scenario.outcomes.filter(
                      (o) => o.impact === 'positive'
                    )
                    const neutralOutcomes = scenario.outcomes.filter((o) => o.impact === 'neutral')
                    const negativeOutcomes = scenario.outcomes.filter(
                      (o) => o.impact === 'negative'
                    )

                    // Calculate weighted impact
                    const weightedImpact = scenario.outcomes.reduce((sum, outcome) => {
                      const impactValue =
                        outcome.impact === 'positive' ? 1 : outcome.impact === 'negative' ? -1 : 0
                      return sum + impactValue * outcome.magnitude * outcome.probability
                    }, 0)

                    // Normalize to -10 to 10 scale
                    const maxPossibleImpact = scenario.outcomes.reduce(
                      (sum, o) => sum + o.magnitude,
                      0
                    )
                    const normalizedImpact =
                      maxPossibleImpact === 0 ? 0 : (weightedImpact / maxPossibleImpact) * 10

                    // Determine impact color
                    let impactColor = 'bg-gray-100 text-gray-800'
                    if (normalizedImpact > 3) impactColor = 'bg-green-100 text-green-800'
                    else if (normalizedImpact < -3) impactColor = 'bg-red-100 text-red-800'
                    else impactColor = 'bg-yellow-100 text-yellow-800'

                    return (
                      <TableRow key={scenario.id}>
                        <TableCell>{scenario.name}</TableCell>
                        <TableCell>{positiveOutcomes.length}</TableCell>
                        <TableCell>{neutralOutcomes.length}</TableCell>
                        <TableCell>{negativeOutcomes.length}</TableCell>
                        <TableCell>
                          <Badge className={impactColor}>{normalizedImpact.toFixed(1)}</Badge>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <div className="scenario-planner my-4 rounded-lg border bg-white p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">{data.title}</h2>
        <p className="text-gray-600">{data.description}</p>
      </div>

      {/* Scenario selection */}
      {renderScenarioList()}

      {/* Factors selection */}
      {renderFactorsList()}

      {/* Active scenario details */}
      {renderScenarioDetails()}

      {/* Comparison view */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Scenario Comparison</h3>
          <Select
            value={compareDimension}
            onValueChange={(value) => setCompareDimension(value as any)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Compare by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="probability">Probability</SelectItem>
              <SelectItem value="factors">Factors</SelectItem>
              <SelectItem value="outcomes">Outcomes</SelectItem>
              <SelectItem value="impact">Impact Analysis</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {renderComparison()}
    </div>
  )
}

export default ScenarioPlanner
